import React from 'react';
import {ClipboardList, Loader2} from 'lucide-react';
import {AppSectionTitle, AppText, AppBodyText, AppSmallText, AppCaption} from '../../../Presentation/components/ui/Typography';
import {formatDate} from '../../../Presentation/utils/dateUtils';
import {useVisitChecklistSubmissions} from '../hooks/useVisitChecklistSubmissions';
import {SUBMISSION_STATUS_LABELS, VisitChecklistSubmission} from '../../checklists/models/checklist.model';

interface VisitChecklistsSectionProps {
  visitId: number;
  /** True when the checklists module is enabled for the client (Salam). */
  enabled: boolean;
}

/**
 * Read-only "قوائم التحقق" results filled during a visit (CR09 Phase 2). Rendered
 * inside the visit detail modal; gated by the checklists module + the
 * `checklist_submissions.read` permission (see VisitDetailsModal).
 */
const VisitChecklistsSection: React.FC<VisitChecklistsSectionProps> = ({visitId, enabled}) => {
  const {submissions, loading, error} = useVisitChecklistSubmissions(visitId, enabled);

  if (!enabled) {
    return null;
  }
  if (loading) {
    return <LoadingBox />;
  }
  if (error) {
    return <ErrorBox />;
  }
  // Nothing filled for this visit — keep the modal uncluttered.
  if (submissions.length === 0) {
    return null;
  }
  return <SubmissionsList submissions={submissions} />;
};

const LoadingBox: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
    <div className="flex items-center gap-3 text-gray-500">
      <Loader2 className="h-5 w-5 animate-spin" />
      <AppBodyText className="text-gray-600">جاري تحميل قوائم التحقق...</AppBodyText>
    </div>
  </div>
);

const ErrorBox: React.FC = () => (
  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
    <AppBodyText className="text-yellow-800">تعذّر تحميل قوائم التحقق لهذه الزيارة.</AppBodyText>
  </div>
);

const SubmissionsList: React.FC<{submissions: VisitChecklistSubmission[]}> = ({submissions}) => (
  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
    <div className="flex items-center gap-3 mb-6">
      <div className="p-2 bg-teal-100 rounded-lg">
        <ClipboardList className="h-5 w-5 text-teal-600" />
      </div>
      <AppSectionTitle>قوائم التحقق</AppSectionTitle>
      <div className="ml-auto">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-100 text-teal-800">
          {submissions.length} قائمة
        </span>
      </div>
    </div>
    <div className="space-y-4">
      {submissions.map(submission => (
        <SubmissionCard key={submission.id ?? submission.checklist_id} submission={submission} />
      ))}
    </div>
  </div>
);

const SubmissionCard: React.FC<{submission: VisitChecklistSubmission}> = ({submission}) => (
  <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
      <AppText className="font-semibold text-gray-900">
        {submission.checklist_name || `قائمة #${submission.checklist_id}`}
      </AppText>
      <div className="flex items-center gap-2">
        <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
          {SUBMISSION_STATUS_LABELS[submission.status] ?? submission.status}
        </span>
        {submission.submitted_at && (
          <AppCaption className="text-gray-500">{formatDate(submission.submitted_at)}</AppCaption>
        )}
      </div>
    </div>
    {submission.answers.length === 0 ? (
      <AppSmallText className="text-gray-500">لا توجد إجابات</AppSmallText>
    ) : (
      <div className="space-y-2">
        {submission.answers.map(answer => (
          <div
            key={answer.checklist_item_id}
            className="flex items-start justify-between gap-4 p-2 bg-white rounded border border-gray-100"
          >
            <AppSmallText className="text-gray-600">
              {answer.item_name || `بند #${answer.checklist_item_id}`}
            </AppSmallText>
            <AppText className="font-medium text-gray-900 text-left">{formatAnswer(answer.value)}</AppText>
          </div>
        ))}
      </div>
    )}
  </div>
);

function formatAnswer (value: string | null): string {
  if (value == null || value.trim() === '') {
    return '—';
  }
  return value;
}

export default VisitChecklistsSection;
